import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/consts/routes";
import { useMemo, useState } from "react";
import { Box, Stack, Divider } from "@mui/material";
import PageTitle from "src/components/Displays/PageTitle";
import InputLabel from "src/components/Displays/InputLabel";
import Text from "src/components/Displays/Text";
import { Trans, useTranslation } from "react-i18next";
import { CancelContractTypes } from "../ContractChange";
import { PaymentDetailsTypes } from "../PaymentDetails";
import useGetContractSummaryData from "./useGetContractSummaryData";
import FormSubmitContainer from "src/components/Shared/FormSubmitContainer";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  contractSummaryWithSmartMeterFormSchema,
  contractSummaryWithoutSmartMeterFormSchema,
} from "src/schema/contractSummaryFormSchema";
import ContractSummaryForm from "src/components/Forms/ContractSummaryForm";
import anonymizeIBAN from "src/helpers/anonymizeIBAN";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { Tariff } from "src/models/clientsModel";
import {
  SESSION_KEYS,
  FUNNEL_ORDER_RESPONSE,
  SITE_TECHNICAL_DATA,
  USER_PROFILE_DATA,
} from "src/consts/session";
import { dateFormat } from "src/helpers/dateFormat";
import usePayloadMapped from "src/dto/usePayloadMapped";
import { funnelOrder, FunnelOrderPayload } from "src/api/apiClients";
import { SmartMeterTypes } from "../SmartMeter";
import { MSG_IDS } from "src/consts/msgIds";
import TarifSelectionContainer from "../Tarif/components/TarifSelectionContainer";

export interface ContractSummaryFormValues {
  wallet: boolean;
  smartMeter?: boolean;
}

const ContractSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [showTariffTimeline] = useState(true);

  const {
    siteTechnicalData: siteTechnicalDataFromStorage,
    userProfileData,
    contractDetailData,
  } = usePayloadMapped();

  const [userProfileDataFromSession] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );

  const [, setFunnelOrderResponse] = useSessionStorage<FUNNEL_ORDER_RESPONSE>(
    SESSION_KEYS.FUNNEL_ORDER_RESPONSE,
  );

  const { tariffsData } = useMemo(() => {
    const tariffsData = getSessionStorageItem();
    return {
      tariffsData: tariffsData,
    };
  }, [getSessionStorageItem]);

  const savingsData = tariffsData && tariffsData[0];
  const monthlyCosts =
    (savingsData && savingsData.fixTariff?.monthlyCosts) ?? "";
  const yearlyCosts = (savingsData && savingsData.fixTariff?.yearlyCosts) ?? "";
  const yearlyGridSupplyKwh =
    userProfileDataFromSession()?.yearlyGridSupplyKwh ?? "";

  const footnote1Text = t("page.tariff.footnotes.footnote1");
  const footnote2Text = t("page.tariff.footnotes.footnote2", {
    yearlyGridSupplyKwh,
    monthlyCosts,
    yearlyCosts,
  });

  const footnoteLastText = t("page.tariff.footnotes.last");

  const handleSubmit = async () => {
    const payload: FunnelOrderPayload = {
      siteTechnicalData: {
        pvInstalled: siteTechnicalDataFromStorage.pvInstalled,
        pvPowerMaxKwp: siteTechnicalDataFromStorage.pvPowerMaxKwp,
        smartMeterInstalled: siteTechnicalDataFromStorage.smartMeterInstalled,
        batteryInstalled: siteTechnicalDataFromStorage.batteryInstalled,
        wallboxInstalled: siteTechnicalDataFromStorage.wallboxInstalled,
        batterySizeKwh: siteTechnicalDataFromStorage.batterySizeKwh,
        marketLocationId: siteTechnicalDataFromStorage.marketLocationId,
        meterNumber: siteTechnicalDataFromStorage.meterNumber,
      },

      userProfileData: {
        firstname: userProfileData.firstname,
        lastname: userProfileData.lastname,
        zipcode: userProfileData.zipcode,
        city: userProfileData.city,
        street: userProfileData.street,
        streetNumber: userProfileData.streetNumber,
        emailAddress: userProfileData.emailAddress,
        mobilePhone: userProfileData.mobilePhone,
        yearlyGridSupplyKwh: siteTechnicalDataFromStorage.yearlyGridSupplyKwh,
        invoiceStreet: userProfileData.invoiceStreet,
        invoiceStreetNumber: userProfileData.invoiceStreetNumber,
        invoiceZipcode: userProfileData.invoiceZipcode,
        invoiceCity: userProfileData.invoiceCity,
        feedinEnumerationInterest: userProfileData.feedinEnumerationInterest,
        pvInstallationYear: userProfileData.pvInstallationYear,
        marketRegistrationId: userProfileData.marketRegistrationId,
        country: userProfileData.country,
      },

      contractDetailData: {
        utilityToCancelExistingContract:
          contractDetailData.utilityToCancelExistingContract,
        paymentMethod: contractDetailData.paymentMethod,
        newContractStartDate: contractDetailData.newContractStartDate,
        energyProviderUuid: contractDetailData.energyProviderUuid,
        agbSmartMeterAccepted: true,
        agbEnergyProviderAccepted: true,
        tariffLogicalId: contractDetailData.tariffLogicalId,
        bankAccountHolder: contractDetailData.bankAccountHolder,
        iban: contractDetailData.iban ? `DE${contractDetailData.iban}` : null,
      },
    };

    setIsLoading(true);

    try {
      const response = await funnelOrder(payload);
      setTimeout(() => {
        setIsLoading(false);
        navigate(ROUTES.CONCLUSION);
      }, 1000);

      setFunnelOrderResponse({
        msgId: response.msgId ?? "",
        redirectUrl: response.redirectUrl ?? "",
      });
    } catch (e) {
      setIsLoading(false);
      const error = e as Error;
      setFunnelOrderResponse({
        msgId: error.msgId ?? "",
        redirectUrl: error.redirectUrl ?? "",
      });

      switch (error.status) {
        case 400:
          navigate(`${ROUTES.ERROR}?msgId=${MSG_IDS.funnel_order_400}`);
          break;
        default:
          navigate(ROUTES.ERROR);
          break;
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const [technicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const isSmartMeterSelected =
    technicalData()?.smartMeterInstalled === SmartMeterTypes.JA;

  const form = useForm<ContractSummaryFormValues>({
    mode: "onSubmit",
    defaultValues: {
      smartMeter: false,
      wallet: false,
    },
    resolver: yupResolver(
      isSmartMeterSelected
        ? contractSummaryWithSmartMeterFormSchema
        : contractSummaryWithoutSmartMeterFormSchema,
    ),
  });

  const {
    tariffProviderDisplayName,
    tariffDisplayName,
    street,
    houseNumber,
    zipCode,
    city,
    mobileNumber,
    email,
    marketLocationID,
    meterNumber,
    cancelContractType,
    energyProvider,
    contractStartDate,
    paymentAccout,
    cardHolder,
    iban,
  } = useGetContractSummaryData();

  return (
    <>
      <Stack
        sx={{
          my: 5,
        }}
      >
        <PageTitle text={t("page.contractSummary.doneBusiness")} />
      </Stack>

      <Box height={12} />

      <Stack gap={4}>
        <Stack>
          <Text
            sx={(theme) => ({
              color: theme.palette.app.primaryFont,
              fontSize: "20px",
              fontWeight: "600",
              lineHeight: "26px",
              fontFamily: "isonType",
              letterSpacing: "-0.4px",
              mb: 4,
            })}
            text={t("page.contractSummary.yourInputs")}
          />
        </Stack>
        <Stack gap={2}>
          <InputLabel text={t("page.contractSummary.newEnergyProductTitle")} />
          <Text
            text={t("page.contractSummary.newEnergyProduct", {
              tariffDisplayName,
              tariffProviderDisplayName,
            })}
          />
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.deliveryAddress")} />
            <Text
              text={t("page.contractSummary.deliveryAddressDetails1", {
                street,
                houseNumber,
              })}
            />
          </Stack>
          <Text
            text={t("page.contractSummary.deliveryAddressDetails2", {
              zipCode,
              city,
            })}
          />
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel
              text={t("page.contractSummary.contactInformationHeader")}
            />
            <Text text={mobileNumber} />
          </Stack>
          <Text text={email} />
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.marketLocationId")} />
            <Text text={marketLocationID} />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.meterNumber")} />
            <Text text={meterNumber} />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel
              text={t("page.contractSummary.terminationOfContract")}
            />
            <Text
              text={
                cancelContractType === CancelContractTypes.AUTO
                  ? t("page.contractSummary.byUtility", { energyProvider })
                  : t("page.contractSummary.byUser")
              }
            />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel
              text={t("page.contractSummary.startElectricityContract")}
            />
            <Text text={dateFormat(contractStartDate)} />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.paymentMethod")} />
            {paymentAccout === PaymentDetailsTypes.SEPA ? (
              <Text text={cardHolder} />
            ) : (
              <Text text={t("page.contractSummary.bankTransfer")} />
            )}
          </Stack>
          {paymentAccout === PaymentDetailsTypes.SEPA && (
            <Text text={anonymizeIBAN(iban)} />
          )}
        </Stack>

        <Divider />

        <Stack my={-6}>
          <TarifSelectionContainer
            showTrustBadges={false}
            showSavingsResult={true}
            tariffsData={tariffsData}
            showTariffTimeline={showTariffTimeline}
            topTextKey="globalContext.order"
          />
        </Stack>

        <Divider />

        <Box
          component="form"
          autoComplete="off"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <ContractSummaryForm
            form={form}
            isSmartMeterSelected={isSmartMeterSelected}
          />
          <FormSubmitContainer
            onClick={handleBack}
            isLoading={isLoading}
            submitButtonText={t("page.contractSummary.submitButtonText")}
          />
        </Box>
        <Stack gap={4}>
          <Text
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            })}
            text={footnote1Text}
          />
          <Text
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            })}
            text={footnote2Text}
          />
          <Box
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
              "& a": {
                color: "the",
                textDecoration: "none",
              },
              "& a:hover": {
                textDecoration: "underline",
              },
            })}
          >
            <Trans
              i18nKey="page.tariff.footnotes.footnote3"
              components={{
                1: (
                  <a
                    href="https://www.lichtblick.de/oekostrom/stromkennzeichnung/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="View electricity labeling"
                  />
                ),
              }}
            />
          </Box>
          <Text
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            })}
            text={footnoteLastText}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default ContractSummary;
